import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ROUTES } from '@/app/config/constants';
import { useAppSelector } from '@/app/store/hooks';
import { canAccess } from '@/core/security/authorization';
import { selectHydrated, selectIsAuthenticated, selectUser } from '@/features/authentication';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { hasServerPermission } from '@/shared/services/auth/serverPermissions';

interface ProtectedRouteProps {
  requiredRoles?: readonly string[];
  /**
   * A server permission code the session must carry, e.g. `WORKFLOW_VIEW`.
   *
   * Separate from `requiredRoles` because the platform's RBAC grants some
   * screens independently of the role: Workflow Configuration is a function
   * with its own row, so a caller can be an administrator and not hold it. Read
   * from the access token's own `perms` claim — the same list the API evaluates
   * — so the two cannot disagree.
   *
   * This hides a page; it does not protect the data. Every endpoint behind it
   * enforces the same code and refuses the request regardless.
   */
  requiredPermission?: string;
}

/**
 * Four-phase guard, in order:
 *   1. not hydrated yet  -> spinner (never a redirect, or a refresh would
 *                           bounce an authenticated user to /login)
 *   2. not authenticated -> /login, remembering where they were headed
 *   3. wrong role        -> /unauthorized
 *   4. missing permission -> /unauthorized
 *
 * A user still on their first password passes through: the shell opens the
 * mandatory Change Password dialog over whichever page this renders.
 */
export function ProtectedRoute({ requiredRoles, requiredPermission }: ProtectedRouteProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const hydrated = useAppSelector(selectHydrated);
  const user = useAppSelector(selectUser);
  const location = useLocation();

  if (!hydrated) {
    return <LoadingSpinner fullPage label="Restoring your session..." />;
  }

  if (!isAuthenticated) {
    /*
      Where they were headed is remembered, for whoever turns out to be coming back.

      Whether it is theirs to return to is not a question this guard can answer: the session has already
      gone, so there is nobody here to compare against. `LoginPage` decides, once somebody has actually
      signed in -- see `AuthState.signedOutUserId`.
    */
    /*
      A supplier's pages send it to the supplier portal's sign-in (2026-10-23) -- the invitation e-mail links to its
      bid, and a supplier signs in with a one-time code, not on the staff screen. Every other page is unchanged.
    */
    const signIn =
      location.pathname.startsWith(ROUTES.SUPPLIER_BIDS) || location.pathname.startsWith(ROUTES.SUPPLIER_DASHBOARD)
        ? ROUTES.SUPPLIER_LOGIN
        : ROUTES.LOGIN;
    return <Navigate to={signIn} state={{ from: location }} replace />;
  }

  if (!canAccess(user?.roles, requiredRoles)) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  if (requiredPermission !== undefined && !hasServerPermission(requiredPermission)) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  return <Outlet />;
}
